<template>
  <div
    class="surface relative flex h-full w-full flex-col justify-between p-2 sm:h-auto sm:max-w-md sm:rounded-md md:max-w-xl"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <!-- Content -->
    <div class="flex min-h-0 grow flex-col space-y-2">
      <!-- Title -->
      <div class="flex items-center justify-between px-1">
        <span class="text-xl select-none">{{ formTitle }}</span>
        <GeneralButton
          title="Sluiten"
          color="surface"
          size="large"
          variant="transparent"
          @click="close"
          :icon="['fas', 'xmark']"
          class="absolute right-0"
        />
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto px-1 sm:max-h-[75dvh]">
        <component :is="component" />
      </div>
    </div>

    <!-- Footer -->
    <div class="grow-0 pt-2">
      <GeneralButton @click="onSaveButtonClick" :disabled="isFormInvalid || isSaving">{{
        isCreating ? "Opslaan" : "Wijzigingen opslaan"
      }}</GeneralButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import type { ModelFormConfig } from "@/interfaces/component/model-form-config.interface"
import { type Component, computed, provide, type Ref, ref } from "vue"
import { clone, upperFirst } from "lodash-es"
import { ModelController } from "@/controllers/model-controllers/abstract/model-controller"
import { onKeyStroke } from "@vueuse/core"
import ProjectModelEditForm from "@/components/project/ProjectModelEditForm.vue"
import PersonModelEditForm from "@/components/person/PersonModelEditForm.vue"
import MachineModelEditForm from "@/components/machine/MachineModelEditForm.vue"
import KnownBusinessEntityModelEditForm from "@/components/known_business_entity/KnownBusinessEntityModelEditForm.vue"
import LocationModelEditForm from "@/components/location/LocationModelEditForm.vue"
import { BaseModel } from "@/models/base.model"
import PurchaseItemModelEditForm from "@/components/purchase_item/PurchaseItemModelEditForm.vue"
import SaleItemModelEditForm from "@/components/sale_item/SaleItemModelEditForm.vue"
import RentalIntakeItemModelEditForm from "@/components/rental_intake_item/RentalIntakeItemModelEditForm.vue"
import RentalDispenseItemModelEditForm from "@/components/rental_dispense_item/RentalDispenseItemModelEditForm.vue"

const props = defineProps<{
  modelFormConfig: ModelFormConfig<BaseModel>
  disabled?: boolean
}>()
const emit = defineEmits(["close"])

onKeyStroke("Escape", close)

// Clone the model for editing
const model = ref(clone(props.modelFormConfig.model))

provide("model", model)
provide("initialText", props.modelFormConfig.textInput)

const controller: Ref<ModelController<any, any, any> | undefined> = ref() // Most use cases
const createHandler: Ref<(() => Promise<void>) | undefined> = ref() // Optional for custom handling
const updateHandler: Ref<(() => Promise<void>) | undefined> = ref() // Optional for custom handling
const customSaveHandler: Ref<((save: () => Promise<void>) => Promise<void>) | undefined> = ref() // Optional for custom handling
provide("controller", controller)
provide("createHandler", createHandler)
provide("updateHandler", updateHandler)
provide("customSaveHandler", customSaveHandler)
provide("closeForm", close)
provide("saveHandle", props.modelFormConfig.saveHandle)
provide("closeHandle", props.modelFormConfig.closeHandle)
provide("creationParameters", props.modelFormConfig.creationParameters)

const isCreating = computed(() => !model.value?.isSaved)
provide("isCreating", isCreating)

const autoClose = ref(true)
provide("autoClose", autoClose)

const isSaving = ref(false)

const component = computed(() => {
  const config = props.modelFormConfig
  // If a custom component is provided, use that
  if (config.component) {
    return config.component
  }

  // Otherwise, use the default component for the custom of inferred model type
  const map: { [key: string]: Component } = {
    Project: ProjectModelEditForm,
    Person: PersonModelEditForm,
    Machine: MachineModelEditForm,
    KnownBusinessEntity: KnownBusinessEntityModelEditForm,
    Location: LocationModelEditForm,
    PurchaseItem: PurchaseItemModelEditForm,
    SaleItem: SaleItemModelEditForm,
    RentalIntakeItem: RentalIntakeItemModelEditForm,
    RentalDispenseItem: RentalDispenseItemModelEditForm
  }
  return map[config.modelType?.className ?? config.model.getClassName()]
})

async function onSaveButtonClick() {
  if (isSaving.value) {
    return
  }
  isSaving.value = true
  try {
    if (customSaveHandler.value) {
      await customSaveHandler.value(save) // Pass save so the custom handler can optionally call it.
    } else {
      await save()
    }

    // Write the saved values back onto the original model instance directly, not
    // via an emit: a custom save handler (e.g. the person form's updateAndClose)
    // may already have closed the form, and an emit from an unmounted component
    // is dropped, silently losing the write-back.
    if (model.value) {
      // eslint-disable-next-line vue/no-mutating-props -- writing the saved values back into the caller's model is this form's contract
      Object.assign(props.modelFormConfig.model, model.value)
    }

    if (props.modelFormConfig.saveHandle) {
      // Be sure to return the original model, because returning the copy will lead to reference issues.
      await props.modelFormConfig.saveHandle(props.modelFormConfig.model, isCreating.value)
    }
  } catch (error) {
    console.error(error)
    throw error
  } finally {
    if (autoClose.value) {
      close()
    }
    isSaving.value = false
  }
}

async function save() {
  if (isCreating.value) {
    await performCreate()
  } else {
    // Important to write back to model, because the backend might have changed some values (e.g. updatedAt, coords)
    model.value = await performUpdate()
  }
}

async function performCreate() {
  if (createHandler.value) {
    return await createHandler.value()
  } else if (controller.value) {
    return await controller.value.create(model.value)
  }
  throw new Error("Cannot create model without controller or createHandler")
}
provide("performCreate", performCreate)

async function performUpdate() {
  if (updateHandler.value) {
    return await updateHandler.value()
  } else if (controller.value) {
    return await controller.value.update(model.value)
  }
  throw new Error("Cannot update model without controller or updateHandler")
}

function close() {
  if (props.modelFormConfig.closeHandle) {
    props.modelFormConfig.closeHandle()
  }
  emit("close")
}

const formTitle = computed(() => {
  const modelName = upperFirst(props.modelFormConfig.model.getVerboseName())
  return `${modelName} ${isCreating.value ? "toevoegen" : "wijzigen"}`
})

const isFormInvalid = ref(false)
provide("isFormInvalid", isFormInvalid)
</script>

<style scoped></style>
